import { useMemo, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import { useSeo } from "@/hooks/useSeo";
import { getByCategory, paginate, sortContent } from "@/lib/content";
import { getCategoryMeta } from "@/mocks/categories";
import CategoryHeader from "@/pages/category/components/CategoryHeader";
import FilterChips from "@/pages/category/components/FilterChips";
import PosterCard from "@/pages/category/components/PosterCard";

const meta = getCategoryMeta("movie-recommend")!;
const PAGE_SIZE = 6;
const allMovies = getByCategory("movie-recommend");

const primaryGenre = (genre?: string) => (genre ? genre.split(" / ")[0] : "");
const genreOptions = Array.from(
  new Set(allMovies.map((m) => primaryGenre(m.genre)).filter(Boolean))
);
const yearOptions = Array.from(
  new Set(allMovies.map((m) => m.year).filter((y): y is string => Boolean(y)))
).sort((a, b) => Number(b) - Number(a));

export default function MovieRecommendPage() {
  const [genre, setGenre] = useState("all");
  const [year, setYear] = useState("all");
  const [page, setPage] = useState(1);

  useSeo({
    title: "电影推荐 - 红桃网",
    description:
      "红桃网电影推荐频道，按类型与年份精选优质影片，从商业大片到文艺佳作，帮你快速找到下一部值得花时间的好电影。",
    keywords: "电影推荐,好电影,影片推荐,红桃网",
  });

  const result = useMemo(() => {
    const filtered = allMovies
      .filter((m) => (genre === "all" ? true : primaryGenre(m.genre) === genre))
      .filter((m) => (year === "all" ? true : m.year === year));
    return paginate(sortContent(filtered, "heat-desc"), page, PAGE_SIZE);
  }, [genre, year, page]);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 w-full">
        <CategoryHeader meta={meta} />
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <div className="rounded-xl bg-background-100 border border-background-200 p-4 md:p-5 mb-6 space-y-4">
            <FilterChips
              label="类型"
              options={genreOptions}
              value={genre}
              allLabel="全部类型"
              onChange={(v) => {
                setGenre(v);
                setPage(1);
              }}
            />
            <FilterChips
              label="年份"
              options={yearOptions}
              value={year}
              allLabel="全部年份"
              onChange={(v) => {
                setYear(v);
                setPage(1);
              }}
            />
          </div>

          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              精选片单
            </h2>
            <span className="text-sm text-foreground-500">
              共 {result.total} 部影片
            </span>
          </div>

          {result.items.length === 0 ? (
            <EmptyState message="暂无符合当前筛选条件的影片，您可以尝试调整类型或年份。" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {result.items.map((item) => (
                <PosterCard key={item.id} item={item} />
              ))}
            </div>
          )}

          <Pagination page={result.page} totalPages={result.totalPages} onChange={setPage} />
        </div>
      </main>
      <Footer />
    </div>
  );
}